import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import {
  EdrTrialReportArrayValue,
  EdrTrialReportData,
  EdrTrialReportingPeriod,
  EndpointsData,
  ExploitedResources,
  IncidentsDistributionBySeverity,
  MonitoredResources,
  ThreatsData,
} from '@nirvana/shared/dtos/types/dashboards/common';
import bb from 'billboard.js/dist/billboard.pkgd';
import ApexCharts from 'apexcharts';
import { dateFormatter } from '@nirvana/shared/time';

export class MspEdrTrialReportView extends BaseWebComponentView {

  constructor(shadow: ShadowRoot, template: HTMLElement) {
    super(shadow, template);
  }

  getDomElements() {
    this.getTranslations();
    this.dom.$clientName = this.shadow.querySelector('#client-name');
    this.dom.$reportingPeriod = this.shadow.querySelector('#reporting-period');
    this.dom.$partnerName = this.shadow.querySelector('#partnerName');
    this.dom.$clientNamePage2 = this.shadow.querySelector('#clientNamePage2');
    this.dom.$reportingPeriodPage2 = this.shadow.querySelector('#reportingPeriodPage2');
    this.dom.$managedEndpoints = this.shadow.querySelector('#managedEndpoints');
    this.dom.$suspiciousIncidents = this.shadow.querySelector('#suspiciousIncidents');
    this.dom.$suspiciousIncidentsShield = this.shadow.querySelector('#suspiciousIncidentsShield');
    this.dom.$incidentsCrossCompany = this.shadow.querySelector('#incidentsCrossCompany');
    this.dom.$incidentsCrossCompanyShield = this.shadow.querySelector('#incidentsCrossCompanyShield');
    this.dom.incidentsChart = this.shadow.querySelector('#incidentsChart');
    this.dom.incidentsChartLegend = this.shadow.querySelector('#incidentsChartLegend');
    this.dom.top5Mitre = this.shadow.querySelector('#top5MitreChart');
    this.dom.top5MitreLegend = this.shadow.querySelector('#top5MitreChartLegend');
    this.dom.threatFamilies = this.shadow.querySelector('#threatFamiliesChart');
    this.dom.top5ExploitedResources = this.shadow.querySelector('#chartExploitedResources');
    this.dom.actorsChart = this.shadow.querySelector('#chartActors');
    this.dom.actorsChartLegend = this.shadow.querySelector('#chartActorsLegend');
    this.dom.$topAffectedDevices = this.shadow.querySelector('.top-affected-devices');
    this.dom.$country = this.shadow.querySelectorAll('.country');
    this.dom.$industry = this.shadow.querySelectorAll('.industry');
    this.dom.$thirdSection = this.shadow.querySelector('#thirdSection');
    this.dom.$fourthSection = this.shadow.querySelector('#fourthSection');
    this.dom.$fifthSection = this.shadow.querySelector('#fifthSection');
    this.dom.$actorsSection = this.shadow.querySelector('#actorsSection');
    this.dom.$threatsSectionTitle = this.shadow.querySelector('#threatsSectionTitle');
    this.dom.$monitoredResources = this.shadow.querySelector('#monitoredResources');
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getDashboardStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  buildSuccessView(mspEdrTrialReport: EdrTrialReportData) {

    this.dom.$clientName.textContent = mspEdrTrialReport.companyName;
    const reportingIntervalString = this.getReportingIntervalString(mspEdrTrialReport.edrTrialReportingPeriod);
    this.dom.$reportingPeriod.textContent = reportingIntervalString;
    this.dom.$partnerName.textContent = mspEdrTrialReport.distributorName;
    this.dom.$clientNamePage2.textContent = mspEdrTrialReport.companyName;
    this.dom.$reportingPeriodPage2.textContent = reportingIntervalString;
    this.dom.$managedEndpoints.textContent = mspEdrTrialReport.endpointsData.endpointsWithEdr;

    if (!mspEdrTrialReport.endpointsData.correlatedIncidents) {
      this.dom.$incidentsCrossCompanyShield.classList.remove('d-none');
      this.dom.$incidentsCrossCompany.classList.add('d-none');
    }

    if (!mspEdrTrialReport.endpointsData.suspiciousIncidents) {
      this.dom.$suspiciousIncidentsShield.classList.remove('d-none');
      this.dom.$suspiciousIncidents.classList.add('d-none');
    }

    this.dom.$suspiciousIncidents.textContent = mspEdrTrialReport.endpointsData.suspiciousIncidents;
    this.dom.$incidentsCrossCompany.textContent = mspEdrTrialReport.endpointsData.correlatedIncidents;
    this.setCountryAndIndustry(mspEdrTrialReport.country, mspEdrTrialReport.industry);

    if (mspEdrTrialReport.endpointsData.endpointsWithEdr !== 0) {
      this.buildEndpointDataSection(mspEdrTrialReport.endpointsData);
    } else {
      this.dom.$thirdSection.classList.add('hidden');
      this.dom.$monitoredResources.classList.add('hidden');
    }

    if (Object.keys(mspEdrTrialReport.threatsData ?? {}).length !== 0) {
      this.buildThreatsDataSection(mspEdrTrialReport.threatsData);
    } else {
      this.dom.$fourthSection.classList.add('hidden');
      this.dom.$fifthSection.classList.add('hidden');
      this.dom.$threatsSectionTitle.classList.add('hidden');
    }
  }

  private buildEndpointDataSection(endpointsData: EndpointsData) {
    if (endpointsData.monitoredResources) {
      this.setMonitoredResources(endpointsData.monitoredResources);
    }

    if (endpointsData.suspiciousIncidents === 0 && endpointsData.correlatedIncidents === 0) {
      this.dom.$thirdSection.classList.add('hidden');
      return;
    }

    if (endpointsData.incidentsDistributionBySeverity) {
      this.buildIncidentsChart(endpointsData.incidentsDistributionBySeverity);
    }

    if (endpointsData.topAffectedDevices?.length) {
      this.setTopAffectedDevices(endpointsData.topAffectedDevices);
    }
  }

  private buildThreatsDataSection(threatsData: ThreatsData) {
    if (threatsData.exploitedResources) {
      this.setTop5ExploitedResources(threatsData.exploitedResources);
    }

    if (threatsData.mitreAttacks) {
      this.buildTop5MitreChart(threatsData.mitreAttacks);
    }

    const shouldDisplayActors = threatsData.actors && threatsData.actors.length > 2;
    if (shouldDisplayActors) {
      this.buildActorsChart(threatsData.actors);
    } else {
      this.dom.$actorsSection.classList.add('hidden');
    }

    if (threatsData.threatFamilies) {
      this.buildThreatFamiliesChart(threatsData.threatFamilies, shouldDisplayActors ? 356 : 749);
    }
  }

  private setCountryAndIndustry(country: string, industry: string) {
    this.dom.$country.forEach((element) => {
      element.textContent = country;
    });
    this.dom.$industry.forEach((element) => {
      element.textContent = industry;
    });
  }

  private setMonitoredResources(monitoredResources: MonitoredResources) {
    Object.entries(monitoredResources).forEach(([key, value]) => {
      this.shadow.querySelector(`#${key}`).textContent = this.formatResourceValue(value);
    });
  }

  private formatResourceValue(num: number): string {
    if (num >= 1000000) {
      return Math.floor(num / 1000000) + 'M';
    } else if (num >= 1000) {
      return Math.floor(num / 1000) + 'k';
    } else {
      return num.toString();
    }
  }

  private getReportingIntervalString(reportingPeriod: EdrTrialReportingPeriod): string {
    const timezone = this.dependencies.getTimezone();
    const intervalStart = dateFormatter(reportingPeriod.startDate * 1000, timezone, 'D MMM YYYY');
    const intervalEnd = dateFormatter(reportingPeriod.endDate * 1000, timezone, 'D MMM YYYY');

    return `${intervalStart} - ${intervalEnd}`;
  }

  private setTop5ExploitedResources(exploitedResources: ExploitedResources) {
    Object.entries(exploitedResources).forEach(([key, value]) => {
      this.dom.top5ExploitedResources.insertAdjacentHTML("beforeend", this.createTop5ExploitedResource(key, value));
    });
  }

  private createTop5ExploitedResource(key: string, value: number) {
    if (key === 'ips') {
      key = 'IPs'; //capitalization reasons
    }

    return `<div class="section-item">
                <div class="resource-name">${key}</div>
                <div class="resource-bar">
                    <div class="resource-bar-fill" style="width: ${value}%"></div>
                </div>
                <div class="resource-value">${value}%</div>
              </div>`;
  }

  private setTopAffectedDevices(topAffectedDevices: Array<EdrTrialReportArrayValue>) {
    if (topAffectedDevices) {
      topAffectedDevices.forEach((device) => {
        this.dom.$topAffectedDevices.insertAdjacentHTML("beforeend", this.createTopAffectedDevices(device));
      });
    }
  }

  private createTopAffectedDevices(device: EdrTrialReportArrayValue) {
    return `<div class="section-item">
                <div class="device-name">${device.name}</div>
                <div class="device-value">${device.value}</div>
              </div>`;
  }

  private buildIncidentsChart(distributionBySeverity: IncidentsDistributionBySeverity) {
    const colors = {
      'highSeverity': '#d23138',
      'mediumSeverity': '#ff883f',
      'lowSeverity': '#ffb74d',
    };

    const columns = [
      ['highSeverity', distributionBySeverity.highSeverity ?? 0],
      ['mediumSeverity', distributionBySeverity.mediumSeverity ?? 0],
      ['lowSeverity', distributionBySeverity.lowSeverity ?? 0],
    ];

    const total = (distributionBySeverity.highSeverity ?? 0) + (distributionBySeverity.mediumSeverity ?? 0) + (distributionBySeverity.lowSeverity ?? 0);

    const nameMap = {
      'highSeverity': 'High',
      'mediumSeverity': 'Medium',
      'lowSeverity': 'Low',
    };

    let legend = `<div class="legend-container">`;
    Object.entries(distributionBySeverity).forEach(([key, value]) => {
      console.log(key, value);
      legend += `<div class="legend-item">
                <div class="legend-item-left-section">
                  <div class="legend-item-left-section-square" style="background-color: ${colors[key]}"></div>
                  <div class="legend-item-left-section-text">${nameMap[key]}</div>
                </div>
                <div class="legend-item-percentage">${value}</div>
              </div>`;
    });
    legend += '</div>';

    bb.generate({
      data: {
        columns: columns,
        type: 'donut',
        colors: colors,
      },
      donut: {
        title: total + "\n\u00a0\nIncidents",
        width: 9,
        padAngle: 0.04,
        expand: false,
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
      legend: {
        show: true,
        position: 'right',
        order: "desc",
        contents: {
          bindto: this.dom.incidentsChartLegend,
          template: legend,
        },
      },
      bindto: this.dom.incidentsChart,
      size: {
        height: 155,
        width: 337,
      },
    });
  }

  private buildTop5MitreChart(mitreAttacks: Array<EdrTrialReportArrayValue>) {
    const defaultColors = [
      '#d23138',
      '#ff883f',
      '#00a19b',
      '#366eb2',
      '#a367dc',
    ];

    const columns = [];
    const colors = {};
    let legend = `<div class="legend-container">`;
    mitreAttacks?.forEach((attack) => {
      colors[attack.name] = defaultColors[columns.length];
      legend += `<div class="legend-item">
                <div class="legend-item-left-section">
                  <div class="legend-item-left-section-square" style="background-color: ${defaultColors[columns.length]}"></div>
                  <div class="legend-item-left-section-text">${attack.name}</div>
                </div>
                <div class="legend-item-percentage">${attack.value}%</div>
              </div>`;
      //@ts-ignore
      columns.push([attack.name, attack.value]);
    });
    legend += '</div>';

    bb.generate({
      data: {
        columns: columns,
        type: 'donut',
        colors: colors,
      },
      donut: {
        width: 9,
        padAngle: 0.04,
        expand: false,
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
      legend: {
        show: true,
        position: 'right',
        order: "desc",
        contents: {
          bindto: this.dom.top5MitreLegend,
          template: legend,
        },
      },
      bindto: this.dom.top5Mitre,
      size: {
        height: 155,
        width: 337,
      },
    });
  }

  private buildActorsChart(actors: Array<EdrTrialReportArrayValue> | undefined) {
    const defaultColors = [
      '#d23138',
      '#ff883f',
      '#00a19b',
      '#366eb2',
      '#a367dc',
    ];

    const columns = [];
    const colors = {};
    let legend = `<div class="legend-container">`;
    actors?.forEach((actor) => {
      colors[actor.name] = defaultColors[columns.length];
      legend += `<div class="legend-item">
                <div class="legend-item-left-section">
                  <div class="legend-item-left-section-square" style="background-color: ${defaultColors[columns.length]}"></div>
                  <div class="legend-item-left-section-text">${actor.name}</div>
                </div>
              </div>`;
      //@ts-ignore
      columns.push([actor.name, actor.value]);
    });
    legend += '</div>';

    bb.generate({
      data: {
        columns: columns,
        type: 'donut',
        colors: colors,
      },
      donut: {
        width: 9,
        padAngle: 0.04,
        expand: false,
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
      legend: {
        show: true,
        position: 'right',
        order: "desc",
        contents: {
          bindto: this.dom.actorsChartLegend,
          template: legend,
        },
      },
      bindto: this.dom.actorsChart,
      size: {
        height: 155,
        width: 337,
      },
    });
  }

  private buildThreatFamiliesChart(threatFamilies: Array<EdrTrialReportArrayValue>, width: number = 720) {
    const data: any[] = [];
    const colors = [
      '#d23138',
      '#ff883f',
      '#00a19b',
      '#366eb2',
      '#a367dc',
      '#EC5B62',
      '#FFD600',
      '#00CCBD',
      '#A2D179',
      '#00CC66',
      '#1B8AA8',
      '#35A9BC',
      '#54CBD1',
      '#6771DC',
      '#67B7DC',
    ];

    threatFamilies.forEach((family) => {
      data.push({
        x: family.name,
        y: family.value,
      });
    });

    const options = {
      colors: colors,
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
        },
      },
      chart: {
        height: 180,
        width: width,
        type: "treemap",
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function(val: number, opts: any) {
          return val + " " + opts.value + "%";
        },
        style: {
          fontFamily: 'Montserrat',
          fontWeight: '400',
          fontSize: '10px',
        },
      },
      series: [
        {
          data: data,
        },
      ],
    };

    const chart = new ApexCharts(this.dom.threatFamilies, options);

    chart.render();
  }
}
